import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditCard, Landmark, Wallet, Building, ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "@/components/motion";
import { api, formatApiError } from "@/lib/api";

const PATHWAYS = [
  { icon: CreditCard, title: "0% APR Business Cards", range: "$25k to $150k", desc: "Stack intro-APR business cards that report to business bureaus only, with zero impact on personal utilization." },
  { icon: Landmark, title: "Term Loans", range: "$50k to $250k", desc: "Fixed-rate term capital from our partner lenders once your profile crosses 700+." },
  { icon: Wallet, title: "Unsecured Personal Lines", range: "$10k to $100k", desc: "Revolving personal credit lines with no collateral, based on your restored personal file." },
  { icon: Building, title: "SBA-Backed Loans", range: "$50k to $500k", desc: "Government-backed capital for established businesses with clean guarantor credit." },
];

const STEPS = [
  { n: "1", title: "Credit Restoration", body: "Metro 2 audits strip the inaccurate negative items dragging your file down." },
  { n: "2", title: "Bureau Structuring", body: "We optimize utilization, age, and mix so your score reads 'lendable' to underwriting algorithms." },
  { n: "3", title: "Lender Matching", body: "Your funding advisor shops your profile across 40+ lending partners with one soft pull, no shotgun applications." },
  { n: "4", title: "Capital Execution", body: "Approvals land. Funds deploy in days, not months. Typical first round: $25k to $250k+." },
];

export default function Funding() {
  const [score, setScore] = useState(640);
  const [revenue, setRevenue] = useState(15000);
  const [form, setForm] = useState({ name: "", email: "", time_in_business: "1-2 years" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const low = score < 620 ? 0 : score < 680 ? 10000 : score < 720 ? 25000 : 50000;
  const high = score < 620 ? 0 : score < 680 ? 50000 : score < 720 ? 150000 : 250000;
  const boost = Math.min(2, revenue / 10000);
  const estLow = Math.round((low * (1 + boost * 0.25)) / 1000) * 1000;
  const estHigh = Math.round((high * (1 + boost * 0.25)) / 1000) * 1000;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/funding/prequal", {
        ...form, score, revenue,
      });
      setResult(data.estimated_range);
      toast.success("Pre-qualification received. A funding advisor will reach out within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="funding-page">
      {/* Hero */}
      <section className="py-28 md:py-36 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.06),transparent_55%)]" />
        <div className="max-w-7xl mx-auto relative">
          <Eyebrow className="mb-6">The Funding Tab</Eyebrow>
          <MaskedLines
            lines={["Credit is the blueprint.", "Funding is the capital."]}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl"
          >
            We don't stop at deletion letters. Once your credit is restored, Clear Path funds what comes next:
            $25,000 to $250,000+ in personal and business capital through our lender network.
          </motion.p>
        </div>
      </section>

      {/* Estimator */}
      <section data-testid="funding-estimator" className="py-24 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Eyebrow>Funding Estimator</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4 mb-10">
              What could you <span className="italic text-emerald">qualify for?</span>
            </h2>
            <div className="space-y-10">
              <div>
                <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">
                  <span>Current Credit Score</span>
                  <span data-testid="estimator-score-value" className="text-gold text-sm">{score}</span>
                </div>
                <input type="range" min="500" max="780" step="5" value={score}
                  data-testid="funding-score-slider"
                  onChange={(e) => setScore(Number(e.target.value))}
                  className="gold-slider w-full"
                  style={{ "--fill": `${((score - 500) / 280) * 100}%` }} />
              </div>
              <div>
                <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">
                  <span>Monthly Revenue</span>
                  <span data-testid="estimator-revenue-value" className="text-gold text-sm">${revenue.toLocaleString()}</span>
                </div>
                <input type="range" min="0" max="80000" step="1000" value={revenue}
                  data-testid="funding-revenue-slider"
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="gold-slider w-full"
                  style={{ "--fill": `${(revenue / 80000) * 100}%` }} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="funding-estimate-result" className="bg-[#101218] border border-white/10 rounded-2xl p-10 text-center shadow-[0_8px_32px_rgba(0,0,0,0.37)] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.08),transparent_60%)]" />
              {estHigh > 0 ? (
                <div className="relative">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Estimated Funding Range</p>
                  <p className="font-serif text-4xl md:text-5xl text-emerald tracking-tight">
                    ${estLow.toLocaleString()} to ${estHigh.toLocaleString()}
                  </p>
                  <p className="text-slate-400 text-sm mt-5 leading-relaxed">
                    {score >= 720
                      ? "Your score is funding-ready. Lender matching can begin immediately."
                      : "Restore your score with Clear Path first. Every 40 points roughly doubles your ceiling."}
                  </p>
                  {score >= 720 && (
                    <span className="inline-block mt-5 bg-emerald/10 text-emerald border border-emerald/30 px-4 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase">
                      Score Target Achieved
                    </span>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Estimated Funding Range</p>
                  <p className="font-serif text-4xl md:text-5xl text-white tracking-tight">Not yet.</p>
                  <p className="text-slate-400 text-sm mt-5 leading-relaxed">
                    Below 620, capital is expensive and scarce. That's exactly why Step 1 is credit restoration.
                    <Link to="/pricing" className="text-gold hover:text-gold-light"> start here</Link>.
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pathways */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <Eyebrow>Funding Pathways</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
              Four doors. <span className="italic text-gold">We hold the keys.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div data-testid={`pathway-card-${i}`} className="group bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 hover:border-emerald/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)] flex gap-6">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald/10 border border-emerald/20 flex items-center justify-center group-hover:bg-emerald/20 transition-colors">
                    <p.icon className="w-5 h-5 text-emerald" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-3 mb-2">
                      <h3 className="font-serif text-xl text-white tracking-tight">{p.title}</h3>
                      <span className="font-mono text-xs text-emerald">{p.range}</span>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <Eyebrow>The Process</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
              From damaged file <span className="italic text-emerald">to deployed capital.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div data-testid={`funding-step-${s.n}`} className="relative bg-[#101218] border border-white/10 rounded-2xl p-8 h-full">
                  <span className="font-serif text-6xl text-white/5 absolute top-4 right-6">{s.n}</span>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-4">Step {s.n}</p>
                  <h3 className="font-serif text-lg text-white mb-3 tracking-tight">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Prequal form */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(93,123,255,0.06),transparent_60%)]" />
        <div className="max-w-2xl mx-auto relative">
          <Reveal className="text-center mb-12">
            <Eyebrow className="mb-4">Pre-Qualification</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white">
              Ready when your score is.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            {result ? (
              <div data-testid="prequal-success" className="bg-[#101218] border border-emerald/40 rounded-2xl p-10 text-center">
                <Check className="w-10 h-10 text-emerald mx-auto mb-5" />
                <h3 className="font-serif text-2xl text-white mb-3">You're in the queue.</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Estimated range: <span className="text-emerald font-medium">${result.low.toLocaleString()} to ${result.high.toLocaleString()}</span>.
                  A funding advisor will contact you within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="prequal-form" className="bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input required placeholder="Full name" value={form.name} data-testid="prequal-name-input"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#171A22] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm" />
                  <input required type="email" placeholder="Email" value={form.email} data-testid="prequal-email-input"
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#171A22] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm" />
                </div>
                <select value={form.time_in_business} data-testid="prequal-time-select"
                  onChange={(e) => setForm({ ...form, time_in_business: e.target.value })}
                  className="w-full bg-[#171A22] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm">
                  {["Not in business yet", "Under 1 year", "1-2 years", "2-5 years", "5+ years"].map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <p className="text-slate-500 text-xs font-mono uppercase tracking-widest">
                  Using estimator values: score {score} · revenue ${revenue.toLocaleString()}/mo
                </p>
                <button type="submit" disabled={submitting} data-testid="prequal-submit-button"
                  className="w-full bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.01] active:scale-[0.99] font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all duration-200 shadow-[0_0_20px_rgba(93,123,255,0.25)] disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? "Submitting…" : "Request Pre-Qualification"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
